import { Log, LogCategory, Operation as LogOperation } from '@augloop/logging';
// Max number of retry attempts for heartbeat
const pingRetryAttemptMax = 3;
// Heartbeat interval
const pingIntervalInMs = 30000;
export class WebSocketReliabilityManager {
    constructor(worker, rateControllerClose, clientMetadata) {
        this.lastPingTime = 0;
        this.lastPongTime = 0;
        this.lastEgressTime = 0;
        this.remainingPingFailures = 3;
        this.isPingPongSuccessful = false;
        this.pingPongLogOp = new LogOperation({ operationName: 'WebSocketReliabilityManager', success: true }).start();
        this.pingPongLogOp.setClientMetadata(clientMetadata);
        this.worker = worker;
        this.rateControllerClose = rateControllerClose;
    }
    start() {
        this.logOperation(true /*success*/, 'ping', 'initial ping');
        this.ping();
        this.isPingPongSuccessful = true;
        this.startInterval();
    }
    onResponse() {
        this.lastPongTime = Date.now();
    }
    isReliabilityResponse(response) {
        return typeof response === 'string' && response.length === 1 && response === WebSocketReliabilityManager.pingPongMessage;
    }
    close() {
        this.logOperation(true /*success*/, 'ping', 'close');
        this.lastPingTime = 0;
        this.lastPongTime = 0;
        this.isPingPongSuccessful = false;
        this.clearPingInterval();
        this.worker = undefined;
    }
    checkConnection() {
        return this.isPingPongSuccessful;
    }
    postEgress() {
        this.lastEgressTime = Date.now();
    }
    needsParsedResponses() {
        return false;
    }
    ping() {
        this.lastPingTime = Date.now();
        if (this.worker) {
            this.worker.egress({ obj: WebSocketReliabilityManager.pingPongMessage });
        }
    }
    startInterval() {
        /*
        The ping-pong message serves as heartbeat detector of the client-server channel. It is set up
        (one time) when network connection has been established, and runs every pingIntervalInMs.
        Due to its periodicity, if a pong message does not arrive before the next time around, it is
        an indication that we may be losing the connection, upon which a couple of retries will be
        attempted before finally giving up and close.

        Note:
        a. No need to ping if no messages are being sent
        b. Start ping again if more messages are being sent
        -------C--------30s---------C--------30s---------C--------30s---------C-------30s------->
        a.   ping    msg  pong
        b.   ping    msg  pong                     msg  ping   msg msg pong
        */
        this.pingInterval = setInterval(() => {
            this.isPingPongSuccessful = (this.lastPongTime >= this.lastPingTime) && (this.lastPongTime - this.lastPingTime <= pingIntervalInMs);
            // Skip pinging if no messages are being sent
            if (this.lastPongTime > this.lastEgressTime) {
                return;
            }
            // Collect ping-pong round trip time
            const pingPongRoundTrip = this.lastPongTime - this.lastPingTime;
            if (this.isPingPongSuccessful) {
                // Reset retry attempt allowed
                this.remainingPingFailures = pingRetryAttemptMax;
                this.ping();
            }
            else {
                if (this.remainingPingFailures > 0) {
                    --this.remainingPingFailures;
                    this.ping();
                }
                else {
                    this.logOperation(false /*success*/, 'ping', 'Pong still not received after all retry attempts', [`${pingPongRoundTrip}`, `${this.remainingPingFailures}`]);
                    // Stop pinging and disconnect
                    if (this.worker) {
                        this.worker.close();
                    }
                    else {
                        this.rateControllerClose();
                    }
                }
            }
        }, pingIntervalInMs);
    }
    clearPingInterval() {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = undefined;
        }
    }
    logOperation(success, resultSignature, resultDescription, dim) {
        var _a, _b, _c, _d;
        this.pingPongLogOp.start();
        this.pingPongLogOp.success = success;
        this.pingPongLogOp.resultSignature = resultSignature;
        this.pingPongLogOp.resultDescription = resultDescription;
        if (dim) {
            this.pingPongLogOp.dimension0 = (_a = dim[0]) !== null && _a !== void 0 ? _a : '';
            this.pingPongLogOp.dimension1 = (_b = dim[1]) !== null && _b !== void 0 ? _b : '';
            this.pingPongLogOp.dimension2 = (_c = dim[2]) !== null && _c !== void 0 ? _c : '';
            this.pingPongLogOp.dimension3 = (_d = dim[3]) !== null && _d !== void 0 ? _d : '';
        }
        Log.info(0x1e3d1709 /* tag_4pr2j */, LogCategory.CoreDefault, this.pingPongLogOp.stop());
    }
}
WebSocketReliabilityManager.pingPongMessage = '~';
//# sourceMappingURL=websocket-reliability-manager.js.map